import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  GoogleAuthProvider,
  getRedirectResult,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
} from 'firebase/auth';
import { auth } from '../../lib/firebase';
import { sanitizeReturnUrl } from '../../lib/safe-return-url';
import { useFunnelLanguage } from './use-funnel-language';

/** Map a Firebase Auth error code to a user-facing i18n key. */
function googleErrorKey(code: string | undefined): string {
  if (code === 'auth/unauthorized-domain') return 'login.error.unauthorizedDomain';
  return 'login.error.google';
}

import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  Alert,
  Link,
  CircularProgress,
  Stack,
  Divider,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { authTheme } from './auth-theme';
import { TeachMark } from '../../components/TeachMark';
import { callable } from '../../lib/callable';

const EMERALD = '#0f766e';
const EMERALD_DARK = '#0b5d56';
const ACCENT = '#10b981';

/** Multicolor Google "G" glyph (MUI has no brand glyph). */
function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}

export function LoginPage() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnUrl = searchParams.get('returnUrl');
  const { language, switchLanguage } = useFunnelLanguage();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resetSent, setResetSent] = useState(false);

  function go() {
    // Sanitize the attacker-controllable returnUrl (open-redirect guard). Falls
    // back to '/' so DefaultRedirect resolves the role-based route.
    const destination = sanitizeReturnUrl(returnUrl) ?? '/';
    navigate(destination, { replace: true });
  }

  // On custom domains the Google flow uses signInWithRedirect (popups are
  // unreliable cross-origin — browser storage partitioning). Pick up the result
  // when the page reloads after the redirect.
  useEffect(() => {
    getRedirectResult(auth)
      .then((result) => {
        if (result?.user) go();
      })
      .catch((e: { code?: string }) => {
        setError(t(googleErrorKey(e?.code)));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      // Go to '/' so DefaultRedirect resolves the role-based route after the
      // membership finishes loading (avoids the fallback-route race).
      go();
    } catch {
      setError(t('login.error.invalidCredentials'));
    } finally {
      setLoading(false);
    }
  }

  async function handleGoogle() {
    setLoading(true);
    setError(null);
    try {
      // Popup for ALL domains (including custom): the redirect handshake breaks
      // under cross-origin storage partitioning when the app domain ≠ authDomain
      // (Safari ITP / Chrome privacy sandbox), which is exactly the multi-domain
      // white-label case. Firebase recommends popup here. Popup is user-gesture
      // initiated, so it isn't caught by popup blockers.
      await signInWithPopup(auth, new GoogleAuthProvider());
      go();
    } catch (e) {
      const code = (e as { code?: string })?.code;
      // In-app webviews / hard blockers can still refuse popups → fall back to
      // the redirect flow (getRedirectResult picks it up on return).
      if (code === 'auth/popup-blocked' || code === 'auth/operation-not-supported-in-environment') {
        try {
          await signInWithRedirect(auth, new GoogleAuthProvider());
          return;
        } catch {
          /* fall through to the generic error below */
        }
      }
      // Silent on user-cancelled popups; surface everything else.
      if (code !== 'auth/popup-closed-by-user' && code !== 'auth/cancelled-popup-request') {
        setError(t(googleErrorKey(code)));
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleForgotPassword() {
    if (!email) {
      setError(t('login.error.emailRequired'));
      return;
    }
    try {
      const resetFn = callable<{ email: string }, { success: boolean }>('requestPasswordReset');
      await resetFn({ email });
      setResetSent(true);
    } catch {
      // Always show success to avoid email enumeration.
      setResetSent(true);
    }
  }

  return (
    <ThemeProvider theme={authTheme}>
      <Box
        sx={{
          position: 'relative',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
          background: 'linear-gradient(135deg, #f6f4ee 0%, #eaf3ef 55%, #e2efe9 100%)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '-12rem',
            right: '-10rem',
            width: '34rem',
            height: '34rem',
            borderRadius: '50%',
            background: `radial-gradient(circle, ${ACCENT}33, transparent 70%)`,
            pointerEvents: 'none',
          },
        }}
      >
        {/* language toggle */}
        <Stack
          direction="row"
          spacing={0.5}
          sx={{ position: 'absolute', top: 20, right: 24 }}
          role="group"
          aria-label={t('language.label')}
        >
          {(['es', 'en'] as const).map((lng) => (
            <Button
              key={lng}
              size="small"
              onClick={() => switchLanguage(lng)}
              sx={{
                minWidth: 40,
                px: 1.25,
                fontWeight: 700,
                fontSize: 12,
                borderRadius: 2,
                color: language === lng ? EMERALD : 'rgba(19,32,29,0.5)',
                background: language === lng ? 'rgba(15,118,110,0.1)' : 'transparent',
              }}
            >
              {lng.toUpperCase()}
            </Button>
          ))}
        </Stack>

        <Card
          sx={{
            width: 420,
            maxWidth: '100%',
            borderRadius: 4,
            border: '1px solid rgba(19,32,29,0.08)',
            boxShadow: '0 24px 60px -24px rgba(19,32,29,0.28)',
            background: '#fffdf8',
          }}
        >
          <CardContent sx={{ p: { xs: 3, sm: 4.5 } }}>
            <Stack alignItems="center" mb={3.5} spacing={1.25}>
              <TeachMark size={52} title="Teach by DosFilos" />
              <Typography variant="h5" fontWeight={800} sx={{ color: '#13201d' }}>
                {t('login.title')}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(19,32,29,0.55)' }}>
                {t('login.subtitle')}
              </Typography>
            </Stack>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            {resetSent && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {t('login.resetSent')}
              </Alert>
            )}

            <form onSubmit={handleLogin}>
              <Stack spacing={2}>
                <TextField
                  label={t('login.email')}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  fullWidth
                  autoComplete="email"
                />
                <TextField
                  label={t('login.password')}
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  fullWidth
                  autoComplete="current-password"
                />
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={loading}
                  sx={{
                    py: 1.25,
                    fontWeight: 700,
                    borderRadius: 2.5,
                    textTransform: 'none',
                    background: `linear-gradient(135deg, ${EMERALD} 0%, ${ACCENT} 100%)`,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${EMERALD_DARK} 0%, ${EMERALD} 100%)`,
                    },
                  }}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : t('login.submit')}
                </Button>
              </Stack>
            </form>

            <Divider sx={{ my: 2.5, color: 'rgba(19,32,29,0.4)', fontSize: 13 }}>
              {t('login.orDivider')}
            </Divider>

            <Button
              onClick={handleGoogle}
              disabled={loading}
              fullWidth
              size="large"
              startIcon={<GoogleIcon />}
              sx={{
                py: 1.25,
                fontWeight: 600,
                borderRadius: 2.5,
                textTransform: 'none',
                color: '#13201d',
                border: '1px solid rgba(19,32,29,0.18)',
                background: '#fff',
                '&:hover': { background: '#f6f4ee', borderColor: 'rgba(19,32,29,0.3)' },
              }}
            >
              {t('login.googleSignIn')}
            </Button>

            <Typography variant="body2" align="center" sx={{ mt: 2.5 }}>
              <Link
                component="button"
                type="button"
                onClick={handleForgotPassword}
                sx={{ color: EMERALD, cursor: 'pointer', textDecorationColor: `${EMERALD}66` }}
              >
                {t('login.forgotPassword')}
              </Link>
            </Typography>
          </CardContent>
        </Card>
      </Box>
    </ThemeProvider>
  );
}
